import React, { useState, useEffect } from "react";
import { Dialog, Button, Divider } from "@mui/material";
import { useTheme } from "../../../../components/hooks/useTheme";
import { post_CreditCard } from "../../../../services/creditCard";
import PleaseWaitPopup from "../../../PleaseWaitPopup";
import useAuth from "../../../../components/hooks/useAuth";
import colorConfigs from "../../../../configs/colorConfigs";
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";

type Props = {
    idNumber: string;
    fullName: string;
    addCreditCardPopup: boolean;
    setAddCreditCardPopup: () => void;
}

export default function AddCreditCard({ idNumber, fullName, addCreditCardPopup, setAddCreditCardPopup }: Props) {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { theme, themeStyles } = useTheme();
    const [paymentGateway, setPaymentGateway] = useState<string>("");
    const [brand, setBrand] = useState<string>("");
    const [creditCard, setCreditCard] = useState<string>("");
    const [cardHolderName, setCardHolderName] = useState<string>("");
    const [expirationDate, setExpirationDate] = useState<string>("");
    const [ccvCode, setCcvCode] = useState<string>("");
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    const resetForm = () => {
        setPaymentGateway("");
        setBrand("");
        setCreditCard("");
        setCardHolderName("");
        setExpirationDate("");
        setCcvCode("");
    };

    useEffect(() => {
        if (addCreditCardPopup) {
            resetForm();
            setCardHolderName(fullName);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addCreditCardPopup]);

    const creditCardValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCreditCard(value);
        const isValid = /^\d{8,19}$/gm.test(value);
        e.target.setCustomValidity(
            isValid ? "" : "Please enetr a valid card number"
        );
    }

    const isValidFormat = (dateStr: string) => {
        const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
        return regex.test(dateStr);
    };

    const isValidExpirationDate = (dateStr: string) => {
        if (!isValidFormat(dateStr)) {
            return false;
        }

        const [monthStr, yearStr] = dateStr.split('/');
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);

        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear() % 100;

        if (year < currentYear || (year === currentYear && month < currentMonth)) {
            return false;
        }

        return true;
    };

    const expirationDateValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
        let { value } = e.target;

        value = value.replace(/\D/g, '');

        if (value.length > 2) {
            value = value.slice(0, 2) + '/' + value.slice(2);
        }
        setExpirationDate(value);

        e.target.setCustomValidity(
            isValidExpirationDate(value) ? "" : "Invalid expiry date. Please enter a valid MM/YY format with a future date."
        );
    };

    const cvvValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCcvCode(value);
        const isValid = /^\d{3,4}$/gm.test(value);
        e.target.setCustomValidity(
            isValid ? "" : "Please enetr a valid cvv number"
        );
    };

    const handleCreditCardDetails = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        await post_CreditCard({
            creditCardNumber: creditCard,
            expirationDate: expirationDate,
            cardholderName: cardHolderName?.trim(),
            paymentGateway: paymentGateway,
            brand: brand,
            ccvCode: ccvCode,
            cardZipCode: "",
            customerId: parseInt(idNumber),
            cClastFourDigit: ""
        }, auth?.id, tenantId)
            .then(() => {
                setPleaseWaitPopup(false);
                setAddCreditCardPopup();
                handleTenantError(null);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                handleTenantError(error);
            });
    };


    return (
        <>
            <Dialog
                open={addCreditCardPopup}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => setAddCreditCardPopup()}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Credit Card</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <form onSubmit={(e) => handleCreditCardDetails(e)}>
                                            <div className="w-full mt-4">
                                                <div className={`pb-4 rounded-lg border ${themeStyles.main.borderColor}`}>
                                                    <span className={`rounded-tl-lg rounded-tr-lg  p-2 inline-block w-full ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg} text-sm`}>
                                                        Payment Details - <strong>USD</strong>
                                                    </span>

                                                    <Divider className={`w-full ${themeStyles.divider.bg}`} />

                                                    <div className="w-full px-2 my-2">
                                                        <div className="w-full flex justify-between items-center gap-x-[0.75rem]">
                                                            <div className="space-y-4 w-[65%]">
                                                                <div>
                                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Credit Card Number</label>
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                        value={creditCard}
                                                                        onChange={creditCardValidation}
                                                                        required
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Cardholder Name</label>
                                                                    <input
                                                                        type="text"
                                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                        value={cardHolderName}
                                                                        onChange={(e) => setCardHolderName(e.target.value)}
                                                                        required
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="space-y-4 flex-1">
                                                                <div>
                                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Expiration Date</label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="MM/YY"
                                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                        value={expirationDate}
                                                                        onChange={expirationDateValidation}
                                                                        required
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>CCV Code</label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="CV"
                                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                        value={ccvCode}
                                                                        onChange={cvvValidation}
                                                                        required
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex gap-4 mt-6">
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: "#039855",
                                                        fontSize: "12px"
                                                    }}
                                                >
                                                    Save Credit Card
                                                </Button>

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => setAddCreditCardPopup()}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>

                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </>
    );
}
